// src/utils/storageUtils.js
import { getStorage, ref, listAll, getMetadata, uploadBytes, getBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { getFirebase, getTestFirebaseForAdmin, getCurrentUserEnv } from '@services/firebase';

/**
 * Returns the appropriate storage bucket name for the current user's env.
 * Test users use the Test project's default storage bucket.
 * Prod users use the provided prodBucket (default: 'pcakeds_csv_backup1').
 */
export const getEnvStorageBucket = (prodBucket = 'pcakeds_csv_backup1') => {
  if (getCurrentUserEnv() === 'Test') {
    return import.meta.env.VITE_FIREBASE_TEST_STORAGE_BUCKET || prodBucket;
  }
  return prodBucket;
};

const MEDIA_EXTENSIONS = new Set(['mp4', 'mov', 'avi', 'mkv', 'webm', 'mp3', 'wav', 'ogg', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'pdf']);
const isMediaFile = (name) => {
  const ext = (name.split('.').pop() || '').toLowerCase();
  return MEDIA_EXTENSIONS.has(ext);
};

// Returns the Firebase app appropriate for the current user's env.
const getAppForEnv = () => {
  try {
    if (getCurrentUserEnv() === 'Test') {
      return getTestFirebaseForAdmin().app;
    }
  } catch (_) {}
  return getFirebase().app;
};

export const listObjectsInFolder = async (prefix = '', bucketName = 'pcakeds_csv_backup1') => {
  const app = getAppForEnv();
  try {
    // Create storage instance with dynamic bucket
    const storage = getStorage(app, `gs://${bucketName}`);
    const folderRef = ref(storage, prefix || '');
    const result = await listAll(folderRef);
    
    return {
      items: result.items.map(item => ({ name: item.fullPath, id: item.fullPath })),
      prefixes: result.prefixes.map(p => ({ name: p.fullPath, id: p.fullPath })),
      bucket: bucketName
    };
  } catch (error) {
    console.error(`Error listing objects in ${prefix || 'root'} of bucket ${bucketName}:`, error.message);
    throw error;
  }
};

/**
 * Like listObjectsInFolder but also fetches size + updated timestamp for each item via getMetadata.
 */
export const listObjectsWithMetadata = async (prefix = '', bucketName = 'pcakeds_csv_backup1') => {
  const app = getAppForEnv();
  try {
    const storage = getStorage(app, `gs://${bucketName}`);
    const folderRef = ref(storage, prefix || '');
    const result = await listAll(folderRef);

    const itemsWithMeta = await Promise.all(
      result.items.map(async (item) => {
        try {
          const meta = await getMetadata(item);
          return {
            name: item.fullPath,
            id: item.fullPath,
            size: meta.size || 0,
            updated: meta.updated || null,
            contentType: meta.contentType || '',
          };
        } catch {
          return { name: item.fullPath, id: item.fullPath, size: 0, updated: null, contentType: '' };
        }
      })
    );

    return {
      items: itemsWithMeta,
      prefixes: result.prefixes.map(p => ({ name: p.fullPath, id: p.fullPath })),
      bucket: bucketName,
    };
  } catch (error) {
    console.error(`Error listing objects with metadata in ${prefix || 'root'} of bucket ${bucketName}:`, error.message);
    throw error;
  }
};

export const readObjectContent = async (objectName, bucketName = 'pcakeds_csv_backup1') => {
  // For media files: getDownloadURL is a read-only SDK op that works for all authenticated users
  // (no Cloud Function needed). This avoids N cold-start CF calls when loading image previews.
  if (isMediaFile(objectName)) {
    const app = getAppForEnv();
    const storage = getStorage(app, `gs://${bucketName}`);
    const fileRef = ref(storage, objectName);
    const downloadUrl = await getDownloadURL(fileRef);
    return {
      downloadUrl,
      contentType: '',
      fileName: objectName.split('/').pop(),
      isMedia: true,
      bucket: bucketName,
      size: 'N/A',
      requiresCorsHandling: false,
    };
  }

  // For non-media files in Test env: use SDK bytes
  if (getCurrentUserEnv() === 'Test') {
    const app = getAppForEnv();
    const storage = getStorage(app, `gs://${bucketName}`);
    const fileRef = ref(storage, objectName);
    const bytes = await getBytes(fileRef);
    return new TextDecoder().decode(bytes);
  }

  const { auth } = getFirebase();
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('No authenticated user. Please log in.');
    }
    const token = await user.getIdToken();
    if (!token) {
      throw new Error('Failed to retrieve ID token.');
    }

    const response = await fetch('https://us-central1-pcakedatastore.cloudfunctions.net/getFileContent', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        path: objectName,
        bucket: bucketName
      }),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Error response:', errorText);
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error?.message || 'Failed to read file');
    }

    // If it's a media file, return the download URL and metadata
    if (result.isMedia) {
      return {
        downloadUrl: result.downloadUrl,
        contentType: result.contentType,
        fileName: result.fileName,
        isMedia: true,
        bucket: result.bucket,
        size: result.size || 'N/A',
        requiresCorsHandling: true
      };
    } else {
      // For data files, return content as string
      return result.content;
    }
  } catch (error) {
    console.error(`Error reading object ${objectName} from bucket ${bucketName}:`, error.message);
    
    // Enhanced fallback: Try multiple approaches
    try {
      // First try: Direct public URL
      const publicUrl = `https://storage.googleapis.com/${bucketName}/${objectName}`;
      
      // Test if the public URL is accessible
      const testResponse = await fetch(publicUrl, { method: 'HEAD' });
      if (testResponse.ok) {
        return {
          downloadUrl: publicUrl,
          contentType: testResponse.headers.get('content-type') || 'video/mp4',
          fileName: objectName.split('/').pop(),
          isMedia: true,
          bucket: bucketName,
          isFallback: true,
          size: testResponse.headers.get('content-length') || 'N/A'
        };
      }
    } catch (fallbackError) {
      console.error('Fallback approach failed:', fallbackError);
    }
    
    // If all fallbacks fail, throw the original error
    throw error;
  }
};

/*
 * Upload file to any storage bucket
 */
export const uploadToStorage = async (fileName, file, contentType, bucketName = 'pcakeds_csv_backup1') => {
  // In Test env, use Firebase Storage SDK directly.
  if (getCurrentUserEnv() === 'Test') {
    return uploadToStorageSdk(fileName, file, contentType, bucketName, getAppForEnv());
  }

  const { auth } = getFirebase();
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('No authenticated user found.');
    const idToken = await user.getIdToken();

    // Convert file to base64 for binary upload
    const fileBuffer = await file.arrayBuffer();
    const base64Content = btoa(
      new Uint8Array(fileBuffer).reduce(
        (data, byte) => data + String.fromCharCode(byte),
        ''
      )
    );

    const payload = {
      fileName: fileName,
      content: base64Content,
      contentType: contentType || file.type || 'application/octet-stream',
      isBinary: true,
      bucketName: bucketName
    };

    const response = await fetch('https://us-central1-pcakedatastore.cloudfunctions.net/writeFile', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Upload response error:', errorText);
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }

    const result = await response.json();

    if (result.success) {
      return {
        success: true,
        message: `File ${fileName} uploaded successfully to ${bucketName}`,
        bucket: bucketName,
        filePath: fileName,
        size: file.size
      };
    } else {
      throw new Error(result.error?.message || 'Unknown error from server');
    }
  } catch (error) {
    console.error(`Error uploading file to ${fileName} in bucket ${bucketName}:`, error);
    throw new Error(`Upload failed: ${error.message}`);
  }
};

/*
 * Get download URL for media files from any bucket
 */
export const getDownloadUrl = async (objectName, bucketName = 'pcakeds_csv_backup1') => {
  // In Test env, use Firebase Storage SDK directly.
  if (getCurrentUserEnv() === 'Test') {
    const storage = getStorage(getAppForEnv(), `gs://${bucketName}`);
    return getDownloadURL(ref(storage, objectName));
  }

  const { auth } = getFirebase();
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('No authenticated user. Please log in.');
    }

    const token = await user.getIdToken();
    
    const response = await fetch('https://us-central1-pcakedatastore.cloudfunctions.net/getFileContent', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        path: objectName,
        bucket: bucketName
      }),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }
    
    const result = await response.json();
    if (result.success && result.isMedia && result.downloadUrl) {
      return result.downloadUrl;
    } else {
      throw new Error('File is not a media file or download URL not available');
    }
  } catch (error) {
    console.error(`Error getting download URL for ${objectName} from bucket ${bucketName}:`, error.message);
    
    // Enhanced fallback with testing
    try {
      const publicUrl = `https://storage.googleapis.com/${bucketName}/${objectName}`;
      
      // Test if the URL is accessible
      const testResponse = await fetch(publicUrl, { method: 'HEAD' });
      if (testResponse.ok) {
        return publicUrl;
      } else {
        throw new Error('Fallback URL not accessible');
      }
    } catch (fallbackError) {
      console.error('Fallback URL also failed:', fallbackError);
      throw error; // Re-throw original error
    }
  }
};

export const getSignedUrl = async (objectName, bucketName = 'pcakeds_csv_backup1') => {
  // Re‑use the logic you already wrote in getDownloadUrl
  // (keeps a single source of truth for auth, error handling, fallback, etc.)
  return await getDownloadUrl(objectName, bucketName);
};

/**
 * Upload a file directly via Firebase Storage SDK to any Firebase app instance.
 * Used for Test-project uploads where no Cloud Function backend exists.
 */
export const uploadToStorageSdk = async (fileName, file, contentType, bucketName, storageApp) => {
  const storage = getStorage(storageApp, `gs://${bucketName}`);
  const storageRef = ref(storage, fileName);
  const metadata = { contentType: contentType || (file && file.type) || 'application/octet-stream' };
  await uploadBytes(storageRef, file, metadata);
  return { success: true, filePath: fileName, bucket: bucketName };
};

/**
 * Rename a file in storage by copying its bytes to the new path then deleting the old one.
 * Returns the download URL of the new file.
 * - Test env: uses Firebase Storage SDK directly.
 * - Prod env: uses Cloud Functions (writeFile + deleteFile) so the user's auth token is forwarded.
 */
export const renameInStorage = async (oldPath, newPath, bucketName = 'pcakeds_csv_backup1') => {
  // ── Test env: SDK has direct access ──────────────────────────────────────
  if (getCurrentUserEnv() === 'Test') {
    const app = getAppForEnv();
    const storage = getStorage(app, `gs://${bucketName}`);
    const oldRef = ref(storage, oldPath);
    const bytes = await getBytes(oldRef);
    let contentType = 'application/octet-stream';
    try {
      const meta = await getMetadata(oldRef);
      contentType = meta.contentType || contentType;
    } catch {}
    const newRef = ref(storage, newPath);
    await uploadBytes(newRef, bytes, { contentType });
    await deleteObject(oldRef);
    return getDownloadURL(newRef);
  }

  // ── Prod env: use Cloud Functions ─────────────────────────────────────────
  const { auth } = getFirebase();
  const user = auth.currentUser;
  if (!user) throw new Error('No authenticated user found');
  const idToken = await user.getIdToken();
  const CF = 'https://us-central1-pcakedatastore.cloudfunctions.net';

  // 1. Get download URL for the source file
  const readResp = await fetch(`${CF}/getFileContent`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
    body: JSON.stringify({ path: oldPath, bucket: bucketName }),
  });
  if (!readResp.ok) throw new Error(`Read failed: ${await readResp.text()}`);
  const readResult = await readResp.json();
  if (!readResult.success) throw new Error(readResult.error?.message || 'Failed to read source file');
  const downloadUrl = readResult.downloadUrl;
  const contentType = readResult.contentType || 'application/octet-stream';

  // 2. Fetch raw bytes from the download URL
  const blobResp = await fetch(downloadUrl);
  if (!blobResp.ok) throw new Error('Failed to fetch file bytes for rename');
  const blob = await blobResp.blob();
  const fileBuffer = await blob.arrayBuffer();
  const base64Content = btoa(
    new Uint8Array(fileBuffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
  );

  // 3. Upload to new path
  const uploadResp = await fetch(`${CF}/writeFile`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
    body: JSON.stringify({ fileName: newPath, content: base64Content, contentType, isBinary: true, bucketName }),
  });
  if (!uploadResp.ok) throw new Error(`Upload failed: ${await uploadResp.text()}`);
  const uploadResult = await uploadResp.json();
  if (!uploadResult.success) throw new Error(uploadResult.error?.message || 'Upload to new path failed');

  // 4. Delete old path
  const deleteResp = await fetch(`${CF}/deleteFile`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
    body: JSON.stringify({ fileName: oldPath, bucketName }),
  });
  if (!deleteResp.ok) throw new Error(`Delete failed: ${await deleteResp.text()}`);

  // 5. Return download URL of the new file
  return getDownloadUrl(newPath, bucketName);
};

export const deleteFromStorage = async (fileName, bucketName = 'pcakeds_csv_backup1') => {
  // In Test env, use Firebase Storage SDK directly.
  if (getCurrentUserEnv() === 'Test') {
    const storage = getStorage(getAppForEnv(), `gs://${bucketName}`);
    await deleteObject(ref(storage, fileName));
    return { success: true };
  }

  const { auth } = getFirebase();
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('No authenticated user found');
    }
    
    const idToken = await user.getIdToken();
    
    const response = await fetch('https://us-central1-pcakedatastore.cloudfunctions.net/deleteFile', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({ 
        fileName: fileName,
        bucketName: bucketName 
      }),
    });
    
    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error?.message || 'Failed to delete file');
    }
    
    return result;
  } catch (error) {
    console.error('Error deleting from storage:', error);
    throw error;
  }
};